import { collection, doc, getDocs, query, setDoc, where, writeBatch } from 'firebase/firestore'
import { firestore, iamAuth } from '@/lib/firebase'
import { TransactionSchema, type Account, type Transaction } from '@/features/transactions/types'

function requireUid(): string {
  const uid = iamAuth.currentUser?.uid
  if (!uid) throw new Error('Not signed in')
  return uid
}

function round2(n: number): number {
  return Math.round(n * 100) / 100
}

export async function fetchAccountTransactions(accountId: string): Promise<Transaction[]> {
  const uid = requireUid()
  const q = query(
    collection(firestore, 'users', uid, 'transactions'),
    where('accountId', '==', accountId),
  )
  const snap = await getDocs(q)
  const rows: Transaction[] = []
  for (const d of snap.docs) {
    const parsed = TransactionSchema.safeParse(d.data())
    if (parsed.success) rows.push(parsed.data)
  }
  return rows
}

export interface ReconcileSummary {
  /** Sum of cleared + reconciled transactions — what the bank should agree with. */
  clearedBalance: number
  unclearedBalance: number
  /** Cleared (not yet reconciled) ids that finishing will lock. */
  toLockIds: string[]
}

export function summarizeForReconcile(txns: Transaction[]): ReconcileSummary {
  let clearedBalance = 0
  let unclearedBalance = 0
  const toLockIds: string[] = []
  for (const t of txns) {
    if (t.cleared === 'uncleared') {
      unclearedBalance += t.amount
    } else {
      clearedBalance += t.amount
      if (t.cleared === 'cleared') toLockIds.push(t.id)
    }
  }
  return {
    clearedBalance: round2(clearedBalance),
    unclearedBalance: round2(unclearedBalance),
    toLockIds,
  }
}

/**
 * Finish reconciliation: lock every cleared transaction to
 * `reconciled`, create a reconciled adjustment transaction if the
 * statement balance differs from the app's cleared balance, and
 * write the corrected balances back onto the account doc (the
 * denormalized balance fields drift when transactions are edited
 * outside Plaid sync — this is the self-heal point).
 */
export async function finishReconcile(
  account: Account,
  statementBalance: number,
  summary: ReconcileSummary,
): Promise<void> {
  const uid = requireUid()
  const adjustment = round2(statementBalance - summary.clearedBalance)

  const ids = [...summary.toLockIds]
  while (ids.length > 0) {
    const chunk = ids.splice(0, 450)
    const batch = writeBatch(firestore)
    for (const id of chunk) {
      batch.set(
        doc(firestore, 'users', uid, 'transactions', id),
        { cleared: 'reconciled' },
        { merge: true },
      )
    }
    await batch.commit()
  }

  if (adjustment !== 0) {
    const tx: Transaction = {
      id: crypto.randomUUID(),
      date: new Date().toISOString().slice(0, 10),
      payeeId: null,
      payeeName: 'Reconciliation Adjustment',
      categoryId: null,
      accountId: account.id,
      amount: adjustment,
      memo: `Reconciled to ${statementBalance.toFixed(2)}`,
      cleared: 'reconciled',
      isInflow: adjustment > 0,
      splits: [],
      isTransfer: false,
      transferAccountId: null,
      transferId: null,
      isCreditCardPayment: false,
      originalCategory: null,
      description: null,
    }
    await setDoc(doc(firestore, 'users', uid, 'transactions', tx.id), tx)
  }

  await setDoc(
    doc(firestore, 'users', uid, 'accounts', account.id),
    {
      clearedBalance: statementBalance,
      unclearedBalance: summary.unclearedBalance,
      workingBalance: round2(statementBalance + summary.unclearedBalance),
    },
    { merge: true },
  )
}
